import { useCallback } from 'react'
import { Box, Button, styled, Typography } from '@mui/material'
// Components
import { showHallProfileMenu } from '$comp/modals/menus/hall-profile-menu'
// logic
import {
  useHall,
  useRoomHasChannelVitrine,
  useRoomIsMookeb,
} from '$logic/hall/react-hook'
import {
  useClientUserPermissions,
  useSubscription,
} from '$logic/subscription/react-hook'
import { useRoom } from '$logic/room/react-hook'
import { closeHallProfile, openHallSettings } from '$logic/router'
import { joinHall, setSubscriptionMute } from '$logic/subscription'
import { openRoom } from '$logic/opened-room'
import { VitrineWappPanel } from '$logic/wapp-panels'
// Common
import { HallProfileInfo } from '$common/hall-profile'
import { RoomProfileHeader } from '$common/room-profile-header'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { ListItemTextButton } from '$common/buttons/list-item-text-button'
import { SettingsSection } from '$common/settings-section'
import { NotificationButton } from '$common/notification-button'
import { VitrineIcon } from '$common/icons'
// Helpers
import { toLocale } from '$func/text'
// Localization
import { stringToColor } from '$func/string-to-color'
import { t } from '$i18n'
import { HallAvatar } from '$common/avatar'
import { OpenStorySource } from '$logic/story/analytics'
import { HallProfileActions } from './hall-profile-actions'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { IS_STORY_ENABLED } from '$func/environmentInfo'
import { useWorkspaceColorByRoomId } from '$logic/workspace'

const StyledVitrineIcon = styled(VitrineIcon)`
  margin-left: 8px;
`

const VitrineButton = styled(Button)`
  width: 100%;
  border-radius: 8px;
  height: 40px;

  &[data-is-mookeb='true'] {
    height: 56px;
    border-radius: 0;

    & > svg,
    & > span {
      color: ${({ theme }) => theme.palette.neutral900};
    }
  }
`

interface Props {
  roomId: string
}

export function HallProfile({ roomId }: Props) {
  const { entity: subscription } = useSubscription(roomId)
  const { entity: hall } = useHall(roomId)
  const { loading: loadingIsMookeb, roomIsMookeb } = useRoomIsMookeb(roomId)

  const { muted } = subscription || {}
  const room = useRoom(roomId)

  const hasChannelVitrine = useRoomHasChannelVitrine(roomId)

  const toggleNotificationStatus = useCallback(async () => {
    // TODO: Show loading modal
    await setSubscriptionMute(roomId, !muted)
  }, [muted, roomId])

  const onJoinClick = useCallback(async () => {
    analite('CHAT_JOIN', { source: 'HALL_PROFILE', name: hall?.identifier })
    // TODO: Show loading modal
    await joinHall(roomId)
    await openRoom(roomId)
  }, [roomId, hall?.identifier])

  const clientUserPermissions = useClientUserPermissions(roomId)
  const showMenu = !(clientUserPermissions?.isOwner && !hall?.public)

  const avatarBackgroundColor = stringToColor(hall?.name, roomId)
  const backgroundColor = useWorkspaceColorByRoomId(roomId)

  return (
    // TODO: CHECK HALL AVATAR COMPONENT
    <PageWithAppBar
      appBar={
        <RoomProfileHeader
          avatarElement={
            <HallAvatar
              roomId={roomId}
              canClientUserCreateStory={
                clientUserPermissions?.isAdmin && room?.type === 'CHANNEL'
              }
              withStory={IS_STORY_ENABLED}
              size={AvatarSize.XL}
              source={OpenStorySource.ROOM_PROFILE}
            />
          }
          primaryText={hall?.name || ''}
          avatarBackground={avatarBackgroundColor}
          hasDeleted={!!hall?.deletedAt}
          onBack={closeHallProfile}
          secondaryText={
            hall?.membersCount
              ? `${toLocale(hall.membersCount)} ${t(
                  `profile.hall.${
                    room?.type === 'CHANNEL' ? 'subscriber' : 'member'
                  }`,
                )}`
              : ''
          }
          onMenu={
            showMenu ? () => showHallProfileMenu({ hallId: roomId }) : undefined
          }
          onSettings={
            clientUserPermissions?.isAdmin
              ? () => openHallSettings(roomId)
              : undefined
          }
          backgroundColor={backgroundColor}
        />
      }
    >
      <Box sx={{ flexGrow: 1, overflow: 'hidden auto' }}>
        <SettingsSection>
          <>
            {hall && <HallProfileInfo hallId={roomId} />}
            {subscription && (
              <NotificationButton
                muted={muted}
                onClick={toggleNotificationStatus}
              />
            )}
          </>
        </SettingsSection>

        {!subscription && hall?.public && (
          <SettingsSection>
            <ListItemTextButton
              onClick={onJoinClick}
              value={
                room?.type === 'CHANNEL'
                  ? t('profile.hall.actions.joinChannel')
                  : t('profile.hall.actions.joinGroup')
              }
            />
          </SettingsSection>
        )}

        {/* TODO : Check upper line condition if is satisfied in hall-profile-actions */}
        <SettingsSection>
          <HallProfileActions
            hallId={roomId}
            isGroup={room?.type === 'GROUP'}
            permissions={clientUserPermissions}
          />
        </SettingsSection>
      </Box>

      {hasChannelVitrine && !loadingIsMookeb && (
        <Box
          sx={{
            bgcolor: 'white',
            flexShrink: 0,
            padding: roomIsMookeb ? 0 : '8px 16px',
            borderTop: '1px solid #E6E6E6',
          }}
        >
          <VitrineButton
            color="secondary"
            variant={roomIsMookeb ? 'contained' : 'outlined'}
            data-is-mookeb={roomIsMookeb}
            onClick={() =>
              VitrineWappPanel.open({ roomId, isMookeb: roomIsMookeb })
            }
          >
            <StyledVitrineIcon sx={{ width: '20px', height: '20px' }} />
            <Typography color="secondary" variant="subtitle3">
              {t('profile.hall.channelVitrine')}
            </Typography>
          </VitrineButton>
        </Box>
      )}
    </PageWithAppBar>
  )
}
